import React, { useEffect, useState } from 'react';
import { IonIcon, IonSpinner, IonText } from '@ionic/react';
import { calendarOutline } from 'ionicons/icons';
import { InspectionScheduleLineItem } from '@/types/response/InspectionScheduleResponses';
import { InspectionModalData } from '@/types/model/inspection-modal';
import { inspectionService } from '@/services/InspectionService';
import { mapInspectionResponseToModalData } from '@/services/util/inspectionModalMapping';
import {
  formatDateParam,
  groupByDate,
  getDayOfWeekShort,
  getDayOfMonth,
  getMonthLabel,
  getMonthKey,
} from '@/services/util/inspectionCalendarUtils';

interface InspectionCalendarPanelProps {
  contractId: number | null;
  inspectionScheduleItems: InspectionScheduleLineItem[];
  isLoading?: boolean;
  error?: string | null;
  onOpenInspection?: (inspectionData: InspectionModalData) => void;
}

const InspectionCalendarPanel: React.FC<InspectionCalendarPanelProps> = ({
  contractId,
  inspectionScheduleItems,
  isLoading = false,
  error = null,
  onOpenInspection,
}) => {
  const todayStr = formatDateParam(new Date());
  const [grouped, setGrouped] = useState<Map<string, InspectionScheduleLineItem[]>>(new Map());

  useEffect(() => {
    if (!contractId || isLoading) {
      setGrouped(new Map());
      return;
    }
    setGrouped(groupByDate(inspectionScheduleItems));
  }, [contractId, inspectionScheduleItems, isLoading]);

  const handleInspectionClick = async (inspectionId: number) => {
    if (!onOpenInspection) return;

    try {
      const [inspectionResponse, filesResponse] = await Promise.all([
        inspectionService.getContractInspection(inspectionId),
        inspectionService.getContractExistingResources(),
      ]);

      const modalData = mapInspectionResponseToModalData(inspectionResponse, filesResponse, {
        status: 'unknown',
        timeSensitivity: 'unknown',
      });

      onOpenInspection(modalData);
    } catch (error) {
      console.error('Failed to load inspection details from calendar click:', error);
    }
  };

  if (!contractId) return null;

  return (
    <div style={{ padding: '0.75rem 1rem' }}>

      {/* Panel header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
        <IonText style={{ fontSize: '0.7rem', fontWeight: '600', letterSpacing: '0.08em', color: 'var(--seesor-light-blue)', textTransform: 'uppercase' }}>
          Inspection Schedule
        </IonText>
        {isLoading && <IonSpinner name="dots" style={{ width: '16px', height: '16px' }} />}
      </div>

      {/* Error */}
      {error && (
        <IonText color="danger" style={{ fontSize: '0.8rem', display: 'block', marginBottom: '0.5rem' }}>
          {error}
        </IonText>
      )}

      {/* Scrollable list */}
      <div style={{ overflowY: 'auto', maxHeight: '480px' }}>

        {/* Empty state */}
        {!isLoading && !error && grouped.size === 0 && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            padding: '2rem 1rem',
            color: 'var(--ion-color-medium)',
          }}>
            <IonIcon icon={calendarOutline} style={{ fontSize: '1.75rem' }} />
            <IonText style={{ fontSize: '0.8rem', textAlign: 'center' }}>
              No inspections in this window
            </IonText>
          </div>
        )}

        {/* Date groups */}
        {(() => {
          let lastMonthKey = '';
          return [...grouped.entries()].map(([dateKey, dayItems]) => {
            const isToday = dateKey === todayStr;
            const isPast = dateKey < todayStr;
            const monthKey = getMonthKey(dateKey);
            const showMonthHeader = monthKey !== lastMonthKey;
            lastMonthKey = monthKey;

            return (
              <div key={dateKey}>

                {/* Month separator */}
                {showMonthHeader && (
                  <div style={{ marginTop: '0.5rem', marginBottom: '0.35rem' }}>
                    <span style={{
                      fontSize: '0.6rem',
                      fontWeight: '300',
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      color: 'var(--ion-color-secondary-tint)',
                    }}>
                      {getMonthLabel(dateKey)}
                    </span>
                  </div>
                )}

                <div style={{ marginBottom: '0.75rem' }}>

                  {/* Date header row */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.4rem' }}>
                    {/* Day column */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '32px', flexShrink: 0 }}>
                      <span style={{
                        fontSize: '0.58rem',
                        fontWeight: '700',
                        letterSpacing: '0.04em',
                        color: isToday ? 'var(--ion-color-primary)' : 'var(--ion-color-medium)',
                        lineHeight: 1.2,
                      }}>
                        {getDayOfWeekShort(dateKey)}
                      </span>
                      <span style={{
                        fontSize: '1.05rem',
                        fontWeight: '700',
                        lineHeight: 1,
                        width: '28px',
                        height: '28px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%',
                        background: isToday ? 'var(--ion-color-primary)' : 'transparent',
                        color: isToday ? '#fff' : (isPast ? 'var(--ion-color-medium)' : 'var(--ion-color-dark)'),
                      }}>
                        {getDayOfMonth(dateKey)}
                      </span>
                    </div>

                    {/* Divider line */}
                    <div style={{ flex: 1, height: '1px', background: 'var(--ion-color-light-shade)' }} />
                  </div>

                  {/* Inspection chips */}
                  <div style={{ paddingLeft: '40px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {dayItems.map(item => (
                      <div
                        key={item.Id}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleInspectionClick(item.Id)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            handleInspectionClick(item.Id);
                          }
                        }}
                        style={{
                          borderRadius: '6px',
                          padding: '5px 8px',
                          background: isPast
                            ? 'var(--ion-color-light-tint)'
                            : 'var(--ion-color-primary)',
                          borderLeft: isPast
                            ? '3px solid var(--ion-color-light-shade)'
                            : '3px solid var(--ion-color-primary-shade)',
                          opacity: isPast ? 0.72 : 1,
                          cursor: 'pointer',
                        }}
                      >
                        <div style={{
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          color: isPast ? 'var(--ion-color-dark)' : '#fff',
                          lineHeight: 1.3,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}>
                          {item.LineItem} — {item.Title}
                        </div>
                        <div style={{
                          fontSize: '0.68rem',
                          color: isPast ? 'var(--ion-color-medium)' : 'rgba(255,255,255,0.82)',
                          lineHeight: 1.3,
                          marginTop: '1px',
                        }}>
                          {item.SiteName} · {item.ExpertiseArea}
                        </div>
                      </div>
                    ))}
                  </div>

                </div>
              </div>
            );
          });
        })()}

      </div>
    </div>
  );
};

export default InspectionCalendarPanel;

